import { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete, apiDownload } from '../services/api';
import TimesheetModal from '../components/TimesheetModal';
import {
  Plus,
  FileText,
  Download,
  Trash2,
  Edit,
  Send,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
} from 'lucide-react';

interface Timesheet {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  status: string;
  total_hours: number;
  total_billable_hours: number;
  notes: string | null;
  rejection_reason: string | null;
}

interface PaginatedResponse {
  items: Timesheet[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-500/10 text-gray-400',
  submitted: 'bg-blue-500/10 text-blue-400',
  approved: 'bg-green-500/10 text-green-400',
  rejected: 'bg-red-500/10 text-red-400',
};

const statusFilters = ['', 'draft', 'submitted', 'approved', 'rejected'];

export default function Timesheets() {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [modalTimesheet, setModalTimesheet] = useState<Timesheet | null | undefined>(undefined);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchTimesheets();
  }, [page, statusFilter]);

  const fetchTimesheets = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('page_size', '20');
      if (statusFilter) params.set('status', statusFilter);

      const data = await apiGet<PaginatedResponse>(`/api/timesheets?${params}`);
      setTimesheets(data.items);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch timesheets:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (id: string) => {
    if (!confirm('Submit this timesheet for approval?')) return;
    try {
      await apiPost(`/api/timesheets/${id}/submit`);
      fetchTimesheets();
    } catch (err: any) {
      alert(err.message || 'Failed to submit');
    }
  };

  const handleApprove = async (id: string) => {
    if (!confirm('Approve this timesheet?')) return;
    try {
      await apiPost(`/api/timesheets/${id}/approve`);
      fetchTimesheets();
    } catch (err: any) {
      alert(err.message || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectId || !rejectReason.trim()) return;
    try {
      await apiPost(`/api/timesheets/${rejectId}/reject`, { reason: rejectReason });
      setRejectId(null);
      setRejectReason('');
      fetchTimesheets();
    } catch (err: any) {
      alert(err.message || 'Failed to reject');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this timesheet?')) return;
    try {
      await apiDelete(`/api/timesheets/${id}`);
      fetchTimesheets();
    } catch (err) {
      console.error('Failed to delete timesheet:', err);
    }
  };

  const handleExportPDF = async (id: string, title: string) => {
    try {
      const blob = await apiDownload(`/api/timesheets/${id}/export/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timesheet_${title.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export PDF:', err);
    }
  };

  const handleExportCSV = async (id: string, title: string) => {
    try {
      const blob = await apiDownload(`/api/timesheets/${id}/export/csv`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timesheet_${title.replace(/\s+/g, '_')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    }
  };

  const formatDate = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Timesheets</h1>
          <p className="text-gray-400">{total} total timesheets</p>
        </div>
        <button
          onClick={() => setModalTimesheet(null)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Timesheet
        </button>
      </div>

      {/* Status filters */}
      <div className="flex gap-2 flex-wrap">
        {statusFilters.map((s) => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
              statusFilter === s
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-gray-400 hover:text-white'
            }`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      {/* Timesheets Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full p-8 text-center text-gray-500">Loading...</div>
        ) : timesheets.length === 0 ? (
          <div className="col-span-full p-8 text-center text-gray-500">No timesheets found</div>
        ) : (
          timesheets.map((ts) => (
            <div key={ts.id} className="bg-dark-900 rounded-xl border border-dark-800 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-indigo-500" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{ts.title}</h3>
                    <p className="text-xs text-gray-500">
                      {formatDate(ts.start_date)} - {formatDate(ts.end_date)}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[ts.status] || statusColors.draft}`}>
                  {ts.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-dark-800 rounded-lg p-2 text-center">
                  <p className="text-xs text-gray-500">Total Hours</p>
                  <p className="text-lg font-bold text-white">{Number(ts.total_hours).toFixed(1)}</p>
                </div>
                <div className="bg-dark-800 rounded-lg p-2 text-center">
                  <p className="text-xs text-gray-500">Billable</p>
                  <p className="text-lg font-bold text-green-400">{Number(ts.total_billable_hours).toFixed(1)}</p>
                </div>
              </div>

              {ts.rejection_reason && ts.status === 'rejected' && (
                <div className="mb-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-xs text-red-400">Rejection reason: {ts.rejection_reason}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 flex-wrap">
                {ts.status === 'draft' && (
                  <>
                    <button
                      onClick={() => setModalTimesheet(ts)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-dark-800 hover:bg-dark-700 text-gray-300 rounded transition-colors"
                    >
                      <Edit className="w-3 h-3" /> Edit
                    </button>
                    <button
                      onClick={() => handleSubmit(ts.id)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded transition-colors"
                    >
                      <Send className="w-3 h-3" /> Submit
                    </button>
                    <button
                      onClick={() => handleDelete(ts.id)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded transition-colors"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </>
                )}
                {ts.status === 'submitted' && (
                  <>
                    <button
                      onClick={() => handleApprove(ts.id)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-green-500/10 hover:bg-green-500/20 text-green-400 rounded transition-colors"
                    >
                      <CheckCircle className="w-3 h-3" /> Approve
                    </button>
                    <button
                      onClick={() => { setRejectId(ts.id); setRejectReason(''); }}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded transition-colors"
                    >
                      <XCircle className="w-3 h-3" /> Reject
                    </button>
                  </>
                )}
                {(ts.status === 'approved' || ts.status === 'submitted') && (
                  <>
                    <button
                      onClick={() => handleExportPDF(ts.id, ts.title)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-dark-800 hover:bg-dark-700 text-gray-300 rounded transition-colors"
                    >
                      <FileText className="w-3 h-3" /> PDF
                    </button>
                    <button
                      onClick={() => handleExportCSV(ts.id, ts.title)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-dark-800 hover:bg-dark-700 text-gray-300 rounded transition-colors"
                    >
                      <Download className="w-3 h-3" /> CSV
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
            >
              <ChevronLeft className="w-5 h-5 text-gray-400" />
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
            >
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* Timesheet Modal */}
      {modalTimesheet !== undefined && (
        <TimesheetModal
          timesheet={modalTimesheet}
          onClose={() => setModalTimesheet(undefined)}
          onSaved={() => {
            setModalTimesheet(undefined);
            fetchTimesheets();
          }}
        />
      )}

      {/* Reject Modal */}
      {rejectId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-900 rounded-xl border border-dark-800 p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-white mb-4">Reject Timesheet</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={3}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setRejectId(null); setRejectReason(''); }}
                className="flex-1 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-gray-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
